import React, { useEffect, useRef, useState } from 'react';
import '../css/german.css';

// Google Apps Script Web App URL (배포 후 복사한 URL로 교체)
const APP_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzlJdKBerQA9zWCtS0C_K0ArEcf3TRE__H6ZVVuz16tZrDvaIRaBvdPLutkezI_OvUL/exec';

const German: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const panelOffsetsRef = useRef<number[]>([]);
  const [showShareIcons, setShowShareIcons] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const currentPageRef = useRef(1);
  const snapTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const wheelLockUntilRef = useRef(0);
  const scrollLockTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchStartPageRef = useRef<number>(1);
  const touchIgnoreRef = useRef(false);
  const [interest, setInterest] = useState<'rent' | 'lease' | ''>('');
  const [email, setEmail] = useState('');
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValidEmail = (value: string) => {
    const v = value.trim();
    // 간단한 이메일 형식 검증 (예: aaa@aaa.aaa)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const handleSubmit = async () => {
    if (!interest) return alert('관심 항목을 선택해주세요.');
    if (!email) return alert('이메일을 입력해주세요.');
    if (!isValidEmail(email)) return alert('이메일 형식을 확인해주세요. (예: aaa@aaa.aaa)');
    if (!agreePrivacy) return alert('개인정보 처리방침에 동의해주세요.');

    try {
      setLoading(true);
      const interestCode = interest === 'lease' ? 1 : 0; // lease=1, rent=0
      const privacyCode = agreePrivacy ? 1 : 0;
      const marketingCode = agreeMarketing ? 1 : 0;

      const payload = {
        interest: interestCode,
        email,
        agreePrivacy: privacyCode,
        agreeMarketing: marketingCode,
      };

      // CORS 회피: no-cors 단일 요청(응답은 읽지 못함)
      const res = await fetch(APP_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });

      const json: any = res.type === 'opaque' ? { ok: true, opaque: true } : await res.json().catch(() => ({}));

      if (json?.ok || json?.opaque) {
        alert('신청 완료되었습니다');
        setInterest('');
        setEmail('');
        setAgreePrivacy(false);
        setAgreeMarketing(false);
      } else {
        console.error('Apps Script error payload', json);
        alert(json?.error || '전송 중 오류가 발생했습니다.');
      }
    } catch (e) {
      console.error('Network error', e);
      alert(`네트워크 오류가 발생했습니다.\n${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPageFromPercentage = (percentage: number) => {
    const p = Math.max(0, Math.min(100, percentage));
    if (p <= 20) return 1;
    if (p <= 40) return 2;
    if (p <= 60) return 3;
    if (p <= 80) return 4;
    return 5;
  };

  const getPeekTopRatioByIndex = (index: number) => {
    // 요구사항:
    // - 1페이지: 위 0%, 아래 4% 노출 (peekTop=0)
    // - 2~4페이지: 위/아래 각각 2% 노출 (peekTop=2%)
    // - 5페이지: 위 4%만 노출 (peekTop=4%)
    if (index === 0) return 0;
    if (index === 4) return 0.04;
    return 0.02;
  };

  const getTargetScrollTop = (container: HTMLDivElement, offsets: number[], index: number) => {
    const peekTop = getPeekTopRatioByIndex(index);
    const peekTopPx = Math.round(container.clientHeight * peekTop);
    const raw = offsets[index] - peekTopPx;
    const maxScroll = container.scrollHeight - container.clientHeight;
    return Math.max(0, Math.min(raw, Math.max(0, maxScroll)));
  };

  const scrollToPage = (pageNumber: number, behavior: ScrollBehavior = 'auto') => {
    const container = containerRef.current;
    if (!container) return;

    let offsets = panelOffsetsRef.current;
    if (offsets.length === 0) {
      const panels = container.querySelectorAll('.panel');
      offsets = panelOffsetsRef.current = Array.from(panels).map((panel) => (panel as HTMLElement).offsetTop);
    }
    if (offsets.length === 0) return;

    const targetIndex = Math.min(Math.max(pageNumber - 1, 0), offsets.length - 1);
    const targetScroll = getTargetScrollTop(container, offsets, targetIndex);

    isScrolling.current = true;
    container.scrollTo({ top: targetScroll, behavior });
    setCurrentPage(targetIndex + 1);
    currentPageRef.current = targetIndex + 1;
    if (scrollLockTimeoutRef.current) window.clearTimeout(scrollLockTimeoutRef.current);
    // smooth 스크롤 중 연속 휠/드래그로 여러 페이지가 "연타"되는 문제 방지
    const lockMs = behavior === 'smooth' ? 650 : 80;
    scrollLockTimeoutRef.current = window.setTimeout(() => {
      scrollLockTimeoutRef.current = null;
      isScrolling.current = false;
    }, lockMs);
  };

  const scrollToPanel5 = () => {
    scrollToPage(5);
  };

  const handleShareClick = () => {
    setShowShareIcons(!showShareIcons);
  };

  const handleKakaoShare = () => {
    // 카카오톡 공유 기능
    const url = window.location.href;
    if ((window as any).Kakao) {
      (window as any).Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '독일 방 구하기 플랫폼',
          description: '사전 예약 신청하기',
          imageUrl: '',
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('링크가 클립보드에 복사되었습니다.');
    }
  };

  const handleInstagramShare = () => {
    // 인스타그램은 직접 링크 공유 불가, URL 복사
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    alert('링크가 클립보드에 복사되었습니다. 인스타그램에 붙여넣기 하세요.');
  };

  const handleLinkShare = async () => {
    // 링크 복사
    const url = 'https://caravan.us.org/dkj';
    try {
      await navigator.clipboard.writeText(url);
      alert('링크가 클립보드에 복사되었습니다.');
    } catch (e) {
      // clipboard API가 막힌 환경 fallback
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('링크가 클립보드에 복사되었습니다.');
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

    const computeOffsets = () => {
      const panels = container.querySelectorAll('.panel');
      panelOffsetsRef.current = Array.from(panels).map((panel) => (panel as HTMLElement).offsetTop);
    };

    const handleScroll = () => {
      if (isScrolling.current) return;

      // 가장 가까운 패널로 현재 페이지 계산
      const offsets = panelOffsetsRef.current;
      if (!offsets.length) return;
      const scrollTop = container.scrollTop;
      let closestIndex = 0;
      let minDistance = Math.abs(getTargetScrollTop(container, offsets, 0) - scrollTop);
      for (let i = 1; i < offsets.length; i++) {
        const dist = Math.abs(getTargetScrollTop(container, offsets, i) - scrollTop);
        if (dist < minDistance) {
          minDistance = dist;
          closestIndex = i;
        }
      }
      setCurrentPage(closestIndex + 1);
      currentPageRef.current = closestIndex + 1;

      // 데스크탑: 스크롤이 멈추면 가장 가까운 페이지로 스냅
      if (!isMobile()) {
        if (snapTimeoutRef.current) window.clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = window.setTimeout(() => {
          snapTimeoutRef.current = null;
          const idx = Math.max(0, Math.min(currentPageRef.current - 1, offsets.length - 1));
          const top = getTargetScrollTop(container, offsets, idx);
          isScrolling.current = true;
          container.scrollTo({ top, behavior: 'smooth' });
          if (scrollLockTimeoutRef.current) window.clearTimeout(scrollLockTimeoutRef.current);
          scrollLockTimeoutRef.current = window.setTimeout(() => {
            scrollLockTimeoutRef.current = null;
            isScrolling.current = false;
          }, 650);
        }, 140);
      }
    };

    // 데스크탑: 휠 한 번 = 한 페이지 이동
    const handleWheel = (e: WheelEvent) => {
      if (isMobile()) return;
      // 트랙패드/마우스 모두에서 일단 "한 번에 한 페이지" 경험을 우선
      if (e.deltaY === 0) return;
      e.preventDefault();
      const now = Date.now();
      if (now < wheelLockUntilRef.current) return;
      wheelLockUntilRef.current = now + 550;
      if (isScrolling.current) return;

      const offsets = panelOffsetsRef.current;
      if (!offsets.length) return;

      const dir = e.deltaY > 0 ? 1 : -1;
      const nextPage = Math.max(1, Math.min(5, currentPageRef.current + dir));
      scrollToPage(nextPage, 'smooth');
    };

    // 모바일: 한 번 스와이프 = 한 페이지 이동(길게 스와이프해도 여러 페이지 점프 방지)
    const shouldIgnoreTouch = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return !!el.closest('input, textarea, select, button, label');
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (!isMobile()) return;
      touchIgnoreRef.current = shouldIgnoreTouch(e.target);
      if (touchIgnoreRef.current) return;
      touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
      touchStartPageRef.current = currentPageRef.current;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isMobile()) return;
      if (touchIgnoreRef.current) return;
      const startY = touchStartYRef.current;
      touchStartYRef.current = null;
      if (startY == null) return;
      const endY = e.changedTouches?.[0]?.clientY ?? startY;
      const delta = startY - endY; // 아래로 스와이프하면 양수
      const threshold = 28; // px
      if (Math.abs(delta) < threshold) return;

      const dir = delta > 0 ? 1 : -1;
      const targetPage = Math.max(1, Math.min(5, touchStartPageRef.current + dir));
      if (targetPage !== currentPageRef.current) {
        scrollToPage(targetPage, 'smooth');
      }
    };

    computeOffsets();
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('resize', computeOffsets);

    // 이미지 로드, 리사이즈에도 오프셋 갱신
    const images = container.querySelectorAll('.panel img');
    images.forEach((img) => img.addEventListener('load', computeOffsets));

    // 레이아웃 변화 감지를 위한 ResizeObserver (미지원 브라우저 방어)
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => computeOffsets()) : null;
    if (resizeObserver) {
      resizeObserver.observe(container);
      container.querySelectorAll('.panel').forEach((panel) => resizeObserver.observe(panel));
    }

    // 한 번 더 지연 실행하여 이미지/폰트 로드 후 오프셋 보정
    setTimeout(computeOffsets, 300);
    setTimeout(computeOffsets, 800);
    
    handleScroll();

    return () => {
      if (snapTimeoutRef.current) window.clearTimeout(snapTimeoutRef.current);
      if (scrollLockTimeoutRef.current) window.clearTimeout(scrollLockTimeoutRef.current);
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', computeOffsets);
      images.forEach((img) => img.removeEventListener('load', computeOffsets));
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div className="app-container" ref={containerRef}>
      {/* 왼쪽 네비게이션 */}
      <div className="page-navigation">
        {[1, 2, 3, 4, 5].map((pageNum) => (
          <button
            key={pageNum}
            className={`nav-dot ${currentPage === pageNum ? 'active' : ''}`}
            onClick={() => scrollToPage(pageNum)}
            aria-label={`페이지 ${pageNum}로 이동`}
          >
            <span className="nav-dot-number">{pageNum}</span>
          </button>
        ))}
      </div>
      
      <div className="panels-wrapper">
        {/* Panel 1: 독일 방 구하기 소개 */}
        <section className="panel panel-1">
          <div className="panel-content">
            <div className="panel-text-section">
              <h2 className="panel-title">
                <span className="panel-title-line">
                  <span className="title-white">독</span><span className="title-blue">일에서</span>
                </span>
                <span className="panel-title-line">
                  <span className="title-white">한</span><span className="title-blue">인 맞춤형</span>
                </span>
                <span className="panel-title-line">
                  <span className="title-white">집</span><span className="title-blue">찾기</span>
                </span>
              </h2>
              <p className="panel-subtitle emphasis">독한집이 독하게 끝냅니다.</p>
              <p className="panel-description panel-description-secondary">
                검증된 쯔비센·장기임대 매물로,<br />안전하게 거래하기
              </p>
            </div>
            
            {/* 모바일 앱 스크린샷 영역 - 패널 2·3과 동일 */}
            <div className="mobile-screenshot mobile-screenshot-right">
              <img src="/images/Phone_1.png" alt="독일 방 구하기 미리보기" />
            </div>
          </div>
        </section>

        {/* Panel 2: 지역별 매물 지도 */}
        <section className="panel panel-2">
          <div className="panel-content">
            <div className="panel-text-section">
              <h2 className="panel-title">
                지역별 매물을<br />한 눈에
              </h2>
              <p className="panel-description">
                원하는 지역 선택 후 주변 매물<br />한 번에 비교하기
              </p>
            </div>
            
            {/* 모바일 앱 스크린샷 영역 - 지도 */}
            <div className="mobile-screenshot mobile-screenshot-right">
              <img src="/images/Phone_2.png" alt="지역별 매물 지도 미리보기" />
            </div>
          </div>
        </section>

        {/* Panel 3: 매물 비교 */}
        <section className="panel panel-3">
          <div className="panel-content">
            <div className="panel-text-section">
              <h2 className="panel-title panel-3-title">
                <span className="panel-title-line">
                  <span className="title-blue">필수 정보를</span><span className="title-white"> 비교</span><span className="title-blue">하고,</span>
                </span>
                <span className="panel-title-line">
                  <span className="title-white">현명하게 결정</span><span className="title-blue">하기</span>
                </span>
              </h2>
              <p className="panel-description">
                판매자 인증마크로 신뢰 up
              </p>
            </div>
            
            {/* 모바일 앱 스크린샷 영역 - 비교 */}
            <div className="mobile-screenshot mobile-screenshot-right">
              <img src="/images/Phone_3.png" alt="매물 비교 미리보기" />
            </div>
          </div>
        </section>

        {/* Panel 4: 사전 예약 혜택 */}
        <section className="panel panel-4-benefits">
          <div className="panel-content">
            <div className="panel-text-section">
              <h2 className="panel-title panel-4-title">독한집이 준비한,<br />사전 예약 혜택</h2>
              <p className="panel-description">
                놓치기 아쉬운 무료 혜택,<br />단 30초면 충분합니다.
              </p>
              <div className="benefits-list">
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>첫 계약 수수료 무료</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>정착 가이드북 PDF 제공</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-check">✓</span>
                  <span>표준 계약서 제공</span>
                </div>
              </div>
            </div>
            
            {/* 일러스트 이미지 영역 */}
            <div className="benefits-illustration">
              <img src="/images/image_1.png" alt="사전 예약 혜택 일러스트" />
            </div>
          </div>
        </section>

        {/* Panel 5: 사전 예약 신청서 */}
        <section className="panel panel-5">
          <div className="panel-content">
            <div className="panel-text-section">
              <div className="panel-5-title-wrapper">
                <div className="form-icon">
                  <img src="/images/image_2.png" alt="사전 예약 신청서 아이콘" />
                </div>
                <h2 className="panel-title">사전 예약 신청서</h2>
              </div>
              <p className="panel-description">
                사전 예약을 위해 정보를 입력해주세요
              </p>
            </div>
            
            {/* 메모지 스타일 폼 (오른쪽) */}
            <div className="memo-paper">
              <div className="memo-form">
                <div className="form-radio-group">
                  <label>
                    <input
                      type="radio"
                      name="interest"
                      value="rent"
                      checked={interest === 'rent'}
                      onChange={() => setInterest('rent')}
                    />
                    <span>집을 구하고 싶어요</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="interest"
                      value="lease"
                      checked={interest === 'lease'}
                      onChange={() => setInterest('lease')}
                    />
                    <span>집을 내놓고 싶어요</span>
                  </label>
                </div>
                <div className="form-email">
                  <label>이메일 *</label>
                  <input
                    type="email"
                    placeholder="example@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-agreements">
                  <label>
                    <input
                      type="checkbox"
                      checked={agreePrivacy}
                      onChange={(e) => setAgreePrivacy(e.target.checked)}
                    />
                    <span>개인정보 처리방침 동의</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={agreeMarketing}
                      onChange={(e) => setAgreeMarketing(e.target.checked)}
                    />
                    <span>마케팅 정보 수신 동의 (선택)</span>
                  </label>
                </div>
                <button
                  className="form-submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? '전송 중...' : '사전 예약 신청하기'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="floating-action-bar">
        <button className="floating-btn floating-btn-primary" onClick={scrollToPanel5}>
          사전 예약하기
        </button>
        <div className="floating-share-group">
          <button className="floating-btn floating-btn-share" onClick={handleShareClick}>
            공유하기
          </button>
          {showShareIcons && (
            <div className="floating-share-icons">
              <button className="share-icon-btn" onClick={handleKakaoShare} title="카카오톡 공유">
                <div className="share-icon kakao">
                  <img src="/images/Kakaotalk.png" alt="KakaoTalk" className="share-icon-img" />
                </div>
              </button>
              <button className="share-icon-btn" onClick={handleInstagramShare} title="인스타그램 공유">
                <div className="share-icon instagram">
                  <img src="/images/Instagram.png" alt="Instagram" className="share-icon-img" />
                </div>
              </button>
              <button className="share-icon-btn" onClick={handleLinkShare} title="링크 복사">
                <div className="share-icon link">
                  <span className="share-icon-text">url</span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default German;

